import {
  AccrualTransaction,
  AccrualStatus,
  AccrualSummary,
  AccrualByCategory,
  AccrualPeriod,
} from '@/types/accrual';

/**
 * Calculate AccrualStatus based on payment state and dates
 */
export function calculateAccrualStatus(
  isPaid: boolean,
  paymentDate?: Date | string | null,
  dueDate?: Date | string | null
): AccrualStatus {
  // Already paid
  if (isPaid && paymentDate) {
    return AccrualStatus.PAID;
  }

  // Not paid - check if overdue
  if (!isPaid && dueDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);

    if (due < today) {
      return AccrualStatus.OVERDUE;
    }
  }

  // Default to pending
  return AccrualStatus.PENDING;
}

/**
 * Transform database transaction record to AccrualTransaction format
 */
export function transformToAccrualTransaction(
  dbRow: any,
  categoryName?: string
): AccrualTransaction {
  // Get competence date with fallback to date
  const competenceDate = dbRow.competence_date
    ? new Date(dbRow.competence_date)
    : new Date(dbRow.date);

  // Map type: income → receita, expense → despesa
  const type = dbRow.type === 'income' ? 'receita' : 'despesa';

  // Get category name from JOIN result or fallback
  const category = categoryName || dbRow.category?.name || 'Sem Categoria';

  // Calculate status
  const status = calculateAccrualStatus(
    dbRow.is_paid,
    dbRow.payment_date,
    dbRow.date // using date as due_date
  );

  // Map recurring frequency
  const recurringFrequency = dbRow.recurrence_frequency as 'monthly' | 'quarterly' | 'yearly' | undefined;

  return {
    id: dbRow.id,
    description: dbRow.description || '',
    amount: Math.abs(Number(dbRow.amount)),
    type,
    category,
    subcategory: undefined,
    due_date: dbRow.date ? new Date(dbRow.date) : new Date(),
    competence_date: competenceDate,
    payment_date: dbRow.payment_date ? new Date(dbRow.payment_date) : undefined,
    status,
    client_name: dbRow.client || undefined,
    supplier_name: dbRow.supplier || undefined,
    payment_method: dbRow.payment_method || undefined,
    document_number: undefined,
    notes: dbRow.notes || undefined,
    created_at: new Date(dbRow.created_at),
    updated_at: new Date(dbRow.updated_at),
    is_recurring: dbRow.is_recurring || false,
    recurring_frequency: recurringFrequency,
    installment_number: dbRow.current_installment || undefined,
    total_installments: dbRow.total_installments || undefined,
  };
}

/**
 * Calculate accrual summary statistics for a period
 */
export function calculateAccrualSummary(
  transactions: AccrualTransaction[],
  period: AccrualPeriod
): AccrualSummary {
  const summary = {
    period,
    total_revenue: 0,
    total_expenses: 0,
    net_result: 0,
    pending_revenue: 0,
    pending_expenses: 0,
    paid_revenue: 0,
    paid_expenses: 0,
    overdue_revenue: 0,
    overdue_expenses: 0,
    transactions_count: transactions.length,
    payment_rate: 0,
  };

  // Calculate totals
  transactions.forEach(t => {
    const amount = Math.abs(t.amount);

    if (t.type === 'receita') {
      summary.total_revenue += amount;

      if (t.status === AccrualStatus.PAID) {
        summary.paid_revenue += amount;
      } else if (t.status === AccrualStatus.PENDING) {
        summary.pending_revenue += amount;
      } else if (t.status === AccrualStatus.OVERDUE) {
        summary.overdue_revenue += amount;
      }
    } else {
      summary.total_expenses += amount;

      if (t.status === AccrualStatus.PAID) {
        summary.paid_expenses += amount;
      } else if (t.status === AccrualStatus.PENDING) {
        summary.pending_expenses += amount;
      } else if (t.status === AccrualStatus.OVERDUE) {
        summary.overdue_expenses += amount;
      }
    }
  });

  // Calculate net result
  summary.net_result = summary.total_revenue - summary.total_expenses;

  // Calculate payment rate
  if (transactions.length > 0) {
    const paidCount = transactions.filter(
      t => t.status === AccrualStatus.PAID || t.status === AccrualStatus.PARTIALLY_PAID
    ).length;
    summary.payment_rate = Math.round((paidCount / transactions.length) * 100);
  }

  return summary;
}

/**
 * Group transactions by category with statistics
 */
export function groupByCategory(
  transactions: AccrualTransaction[]
): AccrualByCategory[] {
  if (transactions.length === 0) {
    return [];
  }

  // Group by category
  const categoryMap = new Map<string, { total: number; count: number }>();

  transactions.forEach(t => {
    const existing = categoryMap.get(t.category) || { total: 0, count: 0 };
    categoryMap.set(t.category, {
      total: existing.total + Math.abs(t.amount),
      count: existing.count + 1,
    });
  });

  // Calculate total for percentages
  const grandTotal = Array.from(categoryMap.values()).reduce(
    (sum, cat) => sum + cat.total,
    0
  );

  // Convert to array with percentages
  return Array.from(categoryMap.entries())
    .map(([category, data]) => ({
      category,
      total_amount: data.total,
      transactions_count: data.count,
      percentage: grandTotal > 0 ? (data.total / grandTotal) * 100 : 0,
    }))
    .sort((a, b) => b.total_amount - a.total_amount);
}

/**
 * Group transactions by status with counts and amounts
 */
export function groupByStatus(
  transactions: AccrualTransaction[]
): Record<AccrualStatus, { count: number; amount: number }> {
  const grouped: Record<AccrualStatus, { count: number; amount: number }> = {
    [AccrualStatus.PENDING]: { count: 0, amount: 0 },
    [AccrualStatus.PAID]: { count: 0, amount: 0 },
    [AccrualStatus.PARTIALLY_PAID]: { count: 0, amount: 0 },
    [AccrualStatus.OVERDUE]: { count: 0, amount: 0 },
    [AccrualStatus.CANCELED]: { count: 0, amount: 0 },
  };

  transactions.forEach(t => {
    grouped[t.status].count++;
    grouped[t.status].amount += Math.abs(t.amount);
  });

  return grouped;
}
